/**
 * Messages whose room key has not reached this device (yet). Like the big messengers, a missing key is first
 * "waiting" (the key usually arrives a moment later), while this device looks for it on its own with growing
 * pauses: it pulls its pending packets again, checks its own encrypted key backup, and asks the sender's device
 * and its own other devices to share the key again (answered only to signed devices, see E2ee.answerKeyRequests).
 * Only when all of that fails is the message shown as not decryptable, with a way to ask again.
 */

/** Pauses before each attempt; after the last one the message counts as failed (about 13 minutes in all). */
export const RETRY_DELAYS_MS = [1_500, 6_000, 30_000, 120_000, 600_000];

export type KeyStatus = 'waiting' | 'failed';

export interface MissingSession {
  sessionId: string;
  conversationId: string;
  /** Who sent the message (user id) and, when known, from which device. */
  senderId: string;
  senderDevice: string | null;
  firstSeen: number;
  /** Attempts made so far. */
  attempts: number;
  failed: boolean;
}

export interface RecoveryDeps {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
  /** One search for the key: pull pending packets, the own backup, ask the devices. Errors are swallowed. */
  attempt(session: MissingSession, how: { round: number; manual: boolean }): Promise<void>;
  /** The status of a message changed (the UI shows waiting / failed). */
  changed(): void;
}

export class KeyRecovery {
  private sessions = new Map<string, MissingSession>();
  private timers = new Map<string, unknown>();
  private running = new Set<string>();

  constructor(private readonly deps: RecoveryDeps) {}

  /** A message could not be decrypted because its room key is missing: start looking (once per session). */
  missing(info: { sessionId: string; conversationId: string; senderId: string; senderDevice?: string | null }): void {
    if (this.sessions.has(info.sessionId)) return;
    const s: MissingSession = { sessionId: info.sessionId, conversationId: info.conversationId, senderId: info.senderId, senderDevice: info.senderDevice ?? null, firstSeen: this.deps.now(), attempts: 0, failed: false };
    this.sessions.set(info.sessionId, s);
    this.schedule(s);
  }

  /** The key arrived (to-device, backup, re-share): stop looking. */
  resolved(sessionId: string): boolean {
    const had = this.sessions.delete(sessionId);
    this.cancel(sessionId);
    return had;
  }

  status(sessionId: string): KeyStatus | null {
    const s = this.sessions.get(sessionId);
    if (!s) return null;
    return s.failed ? 'failed' : 'waiting';
  }

  /** "Ask again": back to waiting and one attempt right away, then the pauses start over. */
  retryNow(sessionId: string): void {
    const s = this.sessions.get(sessionId);
    if (!s) return;
    const wasFailed = s.failed;
    s.failed = false;
    s.attempts = 0;
    this.cancel(sessionId);
    if (wasFailed) this.deps.changed();
    void this.run(s, true);
  }

  /** The app comes back to the front or online: due attempts happen now instead of after their pause. */
  nudge(): void {
    for (const s of this.sessions.values()) if (!s.failed && s.attempts > 0) {
      this.cancel(s.sessionId);
      void this.run(s, false);
    }
  }

  stop(): void {
    for (const id of [...this.timers.keys()]) this.cancel(id);
  }

  /** For tests and diagnostics. */
  get size(): number {
    return this.sessions.size;
  }

  private schedule(s: MissingSession) {
    const delay = RETRY_DELAYS_MS[s.attempts];
    if (delay === undefined) {
      s.failed = true;
      this.deps.changed();
      return;
    }
    this.timers.set(s.sessionId, this.deps.setTimeout(() => {
      this.timers.delete(s.sessionId);
      void this.run(s, false);
    }, delay));
  }

  private cancel(sessionId: string) {
    const t = this.timers.get(sessionId);
    if (t !== undefined) this.deps.clearTimeout(t);
    this.timers.delete(sessionId);
  }

  private async run(s: MissingSession, manual: boolean) {
    if (this.running.has(s.sessionId)) return;
    this.running.add(s.sessionId);
    const round = s.attempts;
    s.attempts += 1;
    try {
      await this.deps.attempt(s, { round, manual });
    } catch {
      /* the next attempt tries again */
    } finally {
      this.running.delete(s.sessionId);
    }
    // Still missing (and not resolved meanwhile): wait for the next attempt, or give up.
    if (this.sessions.get(s.sessionId) === s && !this.timers.has(s.sessionId)) this.schedule(s);
  }
}
